import React, {createContext, useEffect, useMemo, useState} from 'react';
import TaskEditor from "./components/TaskEditor"
import TopNav from './components/TopNav';
import {Impressum, PrivacyStatement} from './components/SiteInfo';
import SideNav from './components/SideNav';
import Welcome from './components/Welcome';
import IdeaBoxView from './components/IdeaBoxView';
import GlyphSketching from './components/GlyphSketching';
import DesignComposition from './components/DesignComposition';
import Presentation from './components/Presentation';
import DialogNewProject from './components/DialogNewProject';
import {Project, ProjectList, Task, TaskType, User} from 'konect-model';
import InfoWindow, {InfoWindowState} from './components/InfoWindow';
import ProjectService from '../services/project-service';
import UserControlService from '../services/user-control-service';
import ShortcutService from '../services/shortcut-service';
import "./App.css";
import InfoPopup from './components/InfoPopup';
import LoginView from './components/LoginView';
import {ErrorHandler} from '../services/api-service';
import CookieConsent from './components/CookieConsent';
import LocalStorageService from '../services/localstorage-service';
import {ViolationService} from '../services/violation-service';
import UserService from '../services/user-service';
import {BrowserRouter, Route, Routes} from "react-router-dom";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import {Settings} from "./Settings";

export enum NavState {
    START = 0,
    LEGAL,
    PRIVACY,
    TASK_EDITOR,
    IDEABOX_PERCEPTION,
    IDEABOX_INTERACTION,
    GLYPH_CREATE_GROUP,
    GLYPH_CREATE_GROUP_TABLE,
    GLYPH_SKETCH,
    GLYPH_COMPOSE,
    PRESENTATION,
    DESIGN_EVALUATION
}

interface AppCtx {
    project: Project;
    setProject: (project: Project) => void;
    changeActiveProject: (project: Project) => void;
    projectList: ProjectList;
    setProjectList: (projectList: ProjectList) => void;
    showInfo: InfoWindowState | undefined;
    setShowInfo: (state: InfoWindowState | undefined) => void;
    navState: NavState;
    setNavState: (state: NavState) => void;
    user: User;
    setUser: (user: User) => void;
    editingTask: Task;
    setEditingTask: (task: Task) => void;
}

//@ts-ignore
export const AppContext = createContext<AppCtx>({});

const App = () => {

    //Impressum/Datenschutz PopUp im Login Screen
    const [legalState, setLegalState] = useState("");

    // state used for dialog window
    const [showDialogNewProject, setShowDialogNewProject] = useState<boolean>(false);

    // hook for setting the dialog window state
    const setShowDialog = (show: boolean) => {
        setShowDialogNewProject(show);
    }

    // state used for the navigation
    const [navState, setNavState] = useState<NavState>(NavState.TASK_EDITOR);

    const [loaded, setLoaded] = useState<boolean>(false);

    const [showInfo, setShowInfo] = useState<InfoWindowState | undefined>(undefined);

    const [projectList, setProjectList] = useState<ProjectList>(new ProjectList());
    const [project, setProjectState] = useState<Project>(new Project());
    const setProject = (project: Project) => {
        setProjectState(Object.assign(new Project(), project));
    }

    const [user, setUser] = useState<User>(LocalStorageService.getUser());

    // task editor states
    const [editingTask, setEditingTask] = useState<Task>(new Task());

    useEffect(() => {
        // Try to restore authentication state from cookies
        const initializeAuth = async () => {
            console.log('Initializing authentication...');
            try {
                const authenticatedUser = await UserService.initUserState();
                if (authenticatedUser) {
                    setUser(authenticatedUser);
                    loadProjectsFromDatabase();
                    UserControlService.initialize(project, setProject, setNavState);
                    ShortcutService.initialize();
                    ViolationService.init();
                } else {
                    // No authenticated user, redirect to login if not already there
                    if (window.location.pathname !== '/login') {
                        // window.location.href = '/login';
                        console.log("Redirect to login")
                    }
                }
            } catch (error) {
                console.error('Failed to initialize authentication:', error);
                // Only redirect if we're not already on the login page
                if (window.location.pathname !== '/login') {
                    //window.location.href = '/login';
                    console.log("Redirect to login")
                }
            }
        };
        console.log("Status OfflineMode: " + Settings.useOfflineMode + "");
        if (!Settings.useOfflineMode) {
            initializeAuth();
        }

        ErrorHandler.onAPIError((error: any) => {
            // Log full error details to developer console
            console.error('API Error:', error);

            // Check if error.response exists before accessing its properties
            if (error.response && error.response.data) {
                // Log detailed error message to developer console
                if (error.response.data.detail) {
                    console.error('Error Details:', error.response.data.detail);
                }
                if (error.response.data.msg) {
                    console.error('Error Message:', error.response.data.msg);
                }

                // Show user-friendly message in popup (works for both authenticated and unauthenticated users)
                UserControlService.showInfoPopup(
                    {
                        title: `${error.response.statusText || 'Server'} Error`,
                        content: `${error.response.data.msg}`,
                        duration: 8000,
                        color: "danger"
                    });
            } else {
                // Handle case where error.response is undefined (network errors, etc.)
                console.error('Network or connection error:', error.message || error);

                UserControlService.showInfoPopup(
                    {
                        title: "Connection Error",
                        content: "Unable to connect to server. Please check your connection or contact the developers at info@konect.dev.",
                        duration: 8000,
                        color: "danger"
                    });
            }
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        //Updates the usercontrol service if the project is changed from an component.
        UserControlService.initialize(project, setProject, setNavState);
    }, [project])

    useEffect(() => {
        if (user && user.username) {
            setLoaded(false);
            loadProjectsFromDatabase();
        }
    }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

    const changeActiveProject = (newProject: Project, currentProjectList?: ProjectList) => {
        setNavState(NavState.TASK_EDITOR);
        UserControlService.reset(newProject);
        const listToUse = currentProjectList || projectList;
        const newProjectList = new ProjectList();
        newProjectList.projects = listToUse.projects || [];
        newProjectList.activeProject = listToUse.activeProject || "";
        newProjectList.setActiveProject(newProject);
        setProjectList(newProjectList);
        setProject(newProject);
        LocalStorageService.setProjectIdToResumeOnReload(newProject.id);
    };

    // Add a parameter to track retry attempts and prevent infinite loops
    const loadProjectsFromDatabase = async (retryCount: number = 0): Promise<any> => {
        // Limit the number of retries to prevent infinite loops
        const MAX_RETRIES = 1;

        let projects: Project[] = [];
        try {
            projects = await ProjectService.getProjectsOfUser(user.username);
            setLoaded(true);

            // Only redirect if we're not already on the home page
            if (window.location.pathname !== '/') {
                //window.location.href = '/';
                console.log("Redirect to home")
            }

            const newProjectList = new ProjectList();
            newProjectList.projects = projects;
            newProjectList.activeProject = projectList.activeProject || "";
            const resume = newProjectList.projects.find(p => p.id === LocalStorageService.getProjectIdToResumeOnReload());
            if (resume) {
                changeActiveProject(resume, newProjectList);
                return;
            }
            changeActiveProject(newProjectList.projects.length > 0 ? newProjectList.projects[0] : new Project(), newProjectList);
        } catch (e: Error | any) {
            console.error('Failed to load projects:', e);
            // Only clear user if it's an authentication error
            if (e.response && e.response.status === 401) {
                // Try to refresh the token first, but only if we haven't exceeded the retry limit
                if (retryCount < MAX_RETRIES) {
                    try {
                        const authenticatedUser = await UserService.initUserState();
                        if (authenticatedUser) {
                            // If we got a user, try loading projects again with incremented retry count
                            return loadProjectsFromDatabase(retryCount + 1);
                        }
                    } catch (refreshError) {
                        console.error('Failed to refresh authentication:', refreshError);
                    }
                } else {
                    console.error('Max retry attempts reached. Redirecting to login.');
                }

                // If refresh failed, max retries reached, or no user was returned, clear the user
                setUser(new User());
                // Redirect to login page if authentication failed
                if (window.location.pathname !== '/login') {
                    //window.location.href = '/login';
                    console.log("Redirect to login")
                }
            }
            setLoaded(true);
        }
    }

    const createProject = async (name: string): Promise<boolean> => {
        const newProject = await ProjectService.createProject(name, user.username);
        if (!newProject) return false;
        const newProjectList = new ProjectList();
        newProjectList.projects = projectList.projects || [];
        newProjectList.activeProject = projectList.activeProject || "";
        newProjectList.addProject(newProject);
        setProjectList(newProjectList);
        changeActiveProject(newProject, newProjectList);
        UserControlService.showInfoPopup({
            title: "New Project",
            content: `Project ${newProject.name} was successfully created!`,
            duration: 5000
        });
        return true;
    }

    const renderStep = useMemo(() => {
        if (!loaded) {
            return <i className={"fas fa-spinner start-spinner"}/>;
        }
        switch (navState) {
            case NavState.START:
                return <Welcome setShowProjectDialog={setShowDialog}/>
            case NavState.LEGAL:
                return <Impressum/>;
            case NavState.PRIVACY:
                return <PrivacyStatement/>;
        }

        if (!project.name) {
            return <Welcome setShowProjectDialog={setShowDialog}/>
        }

        switch (navState) {
            case NavState.TASK_EDITOR:
                return <TaskEditor/>;
            case NavState.IDEABOX_PERCEPTION:
                return <IdeaBoxView type={TaskType.INFORMATION}/>;
            case NavState.IDEABOX_INTERACTION:
                return <IdeaBoxView type={TaskType.INTERACTION}/>;
            case NavState.GLYPH_CREATE_GROUP:
            case NavState.GLYPH_CREATE_GROUP_TABLE:
            case NavState.GLYPH_SKETCH:
            case NavState.GLYPH_COMPOSE:
                return <GlyphSketching state={navState}/>;
            case NavState.PRESENTATION:
                return <Presentation/>;
            case NavState.DESIGN_EVALUATION:
                return <DesignComposition/>;
        }
    }, [navState, project, loaded]);

    // memoized context properties to prevent unnecessary rerenders
    const contextValue = React.useMemo(() => ({
        project,
        setProject,
        changeActiveProject,
        projectList,
        setProjectList,
        showInfo,
        setShowInfo,
        navState,
        setNavState,
        user,
        setUser,
        editingTask,
        setEditingTask
    }), [project, projectList, showInfo, navState, user, editingTask]); // eslint-disable-line react-hooks/exhaustive-deps

    let login;
    login = (<>
            <main className='main-container'>
                <LoginView legalState={[legalState, setLegalState]}/>
            </main>
            <InfoPopup/>
            <CookieConsent/>
            <Footer/>
        </>
    );


    // @ts-ignore
    return (
        <AppContext.Provider value={contextValue}>
            <BrowserRouter 		future={{
		        v7_startTransition: true,
		        v7_relativeSplatPath: true,
		    }}>
                <Routes>
                    {/* Öffentliche Routen */}
                    <Route path="/login" element={login}/>

                    {/* Geschützte Routen */}
                    <Route path="/" element={
                        <ProtectedRoute>

                            <TopNav setShowProjectDialog={setShowDialog} onLoad={loadProjectsFromDatabase}
                                    loginView={!user.username}
                                    navState={navState}/>
                            <main className='main-container'>
                                <>
                                    <SideNav setShowProjectDialog={setShowDialog} navState={navState}
                                             onLoad={loadProjectsFromDatabase}/>
                                    <div className="step-container p-4">
                                        <DialogNewProject setShowProjectDialog={setShowDialog}
                                                          showDialog={showDialogNewProject}
                                                          createNewProject={createProject}/>
                                        {renderStep}
                                    </div>
                                    <InfoWindow onClose={() => {
                                        setShowInfo(undefined)
                                    }}/>
                                </>
                                <InfoPopup/>
                            </main>
                            <CookieConsent/>


                        </ProtectedRoute>
                    }/>
                    {/* Weitere geschützte Routen */}
                </Routes>
            </BrowserRouter>
        </AppContext.Provider>
    );


};

export default App;
